/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import update from 'immutability-helper';
import { tableActions, baseTableReducer } from 'carbon-addons-iot-react';

import { ROUTES } from '../../Common/constants/constants';

import {
  GET_PLATFORM_TYPES,
  GET_PLATFORM_TYPES_FAILURE,
  GET_PLATFORM_TYPES_SUCCESS,
  GET_PLATFORM_TYPE,
  GET_PLATFORM_TYPE_FAILURE,
  GET_PLATFORM_TYPE_SUCCESS,
  UPDATE_TYPE_SHOW_DELETE_CONFIRM,
  DELETE_PLATFORM_TYPE,
  DELETE_PLATFORM_TYPE_SUCCESS,
  DELETE_PLATFORM_TYPE_FAILURE,
  TYPES_TABLE_SET_PAGE,
  START_PLATFORM_TYPE_DETAILS_EDIT_MODE,
  CANCEL_PLATFORM_TYPE_DETAILS_EDIT_MODE,
  GET_PLATFORM_TYPE_PI,
  GET_PLATFORM_TYPE_PI_SUCCESS,
  GET_PLATFORM_TYPE_PI_FAILURE,
  GET_PLATFORM_TYPE_ETS,
  GET_PLATFORM_TYPE_ETS_SUCCESS,
  GET_PLATFORM_TYPE_ETS_FAILURE,
  GET_PLATFORM_TYPE_ETMS,
  GET_PLATFORM_TYPE_ETMS_SUCCESS,
  GET_PLATFORM_TYPE_ETMS_FAILURE,
  SAVE_PLATFORM_TYPE_DETAILS_EDITS,
  SAVE_PLATFORM_TYPE_DETAILS_EDITS_SUCCESS,
  SAVE_PLATFORM_TYPE_DETAILS_EDITS_FAILURE,
  SET_SELECTED_PI_EVENT_TYPE_ID,
  SET_SELECTED_MAPPING_LI_ID,
  GET_PLATFORM_TYPE_DIFF,
  GET_PLATFORM_TYPE_DIFF_SUCCESS,
  GET_PLATFORM_TYPE_DIFF_FAILURE,
  GET_PLATFORM_TYPE_VALIDATION,
  GET_PLATFORM_TYPE_VALIDATION_SUCCESS,
  GET_PLATFORM_TYPE_VALIDATION_FAILURE,
  CLEAR_PLATFORM_TYPE_DETAILS,
  GET_PLATFORM_TYPE_MAPPINGS,
  GET_PLATFORM_TYPE_MAPPINGS_SUCCESS,
  GET_PLATFORM_TYPE_MAPPINGS_FAILURE,
  GET_PLATFORM_TYPE_LIS,
  GET_PLATFORM_TYPE_LIS_SUCCESS,
  GET_PLATFORM_TYPE_LIS_FAILURE,
  GET_PLATFORM_TYPE_SCHEMAS,
  GET_PLATFORM_TYPE_SCHEMAS_SUCCESS,
  GET_PLATFORM_TYPE_SCHEMAS_FAILURE,
  TYPES_TABLE_CLEAR_REDIRECT,
  TYPES_TABLE_CLEAR_SEARCH,
} from 'typesActionTypes';

export const getInitialDetailState = () => ({
  fetchingData: false,
  fetchDataError: null,
  inEditMode: false,
  isSaving: false,
  saveError: undefined,

  // everything pertaining to the PI of the device type
  // (pi, ets, etms, schemas)
  pi: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  ets: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  etms: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },

  diff: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  validation: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },

  type: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },

  // everything pertaining to the LIs of the device type
  // (mappings, lis, schemas)
  lis: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  mappings: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  schemas: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  selectedEventTypeId: undefined,
  selectedMappingLiId: undefined,
  data: [],
});

export const typesReducer = (
  state = {
    table: {
      data: [],
      fetchingData: false,
      fetchDataError: null,
      tableState: {
        instanceId: 'typesTable',
        data: [],
        view: {
          filters: [],
          pagination: {
            pageSizes: [20, 50, 100],
            pageSize: 20,
            page: 1,
            totalItems: 0,
            maxPages: 10000000,
          },
          table: {
            isSelectAllSelected: false,
            sort: undefined,
            selectedIds: [],
            expandedIds: [],
            shouldApplyDynamicOrdering: true,
            loadingState: {
              isLoading: false,
              rowCount: 20, // page size
            },
          },
          toolbar: {
            batchActions: [
              {
                id: 'delete-types',
                labelText: 'Common.Delete',
                icon: 'delete',
                iconDescription: 'Common.Delete',
              },
            ],
            search: {
              defaultValue: undefined,
            },
          },
        },
      },
    },
    showDeleteConfirm: false,
    // allows reducer to invoke history.push in response to events
    redirectTo: undefined,
    initialTableSearchDefaultValue: undefined,
    detail: getInitialDetailState(),
  },
  action
) => {
  switch (action.type) {
    case CLEAR_PLATFORM_TYPE_DETAILS: {
      return update(state, {
        detail: { $set: getInitialDetailState() },
      });
    }

    case GET_PLATFORM_TYPE_SUCCESS:
      return update(state, {
        detail: {
          type: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    case GET_PLATFORM_TYPE:
      return update(state, {
        detail: {
          type: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    case GET_PLATFORM_TYPE_FAILURE:
      return update(state, {
        detail: {
          type: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });

    case GET_PLATFORM_TYPE_MAPPINGS: {
      return update(state, {
        detail: {
          mappings: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_MAPPINGS_SUCCESS: {
      return update(state, {
        detail: {
          mappings: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_MAPPINGS_FAILURE: {
      return update(state, {
        detail: {
          mappings: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_LIS: {
      return update(state, {
        detail: {
          lis: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_LIS_SUCCESS: {
      return update(state, {
        detail: {
          lis: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_LIS_FAILURE: {
      return update(state, {
        detail: {
          lis: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_PI: {
      return update(state, {
        detail: {
          pi: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_PI_SUCCESS: {
      return update(state, {
        detail: {
          pi: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_PI_FAILURE: {
      return update(state, {
        detail: {
          pi: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_ETMS: {
      return update(state, {
        detail: {
          etms: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_ETMS_SUCCESS: {
      return update(state, {
        detail: {
          etms: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_ETMS_FAILURE: {
      return update(state, {
        detail: {
          etms: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_ETS: {
      return update(state, {
        detail: {
          ets: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_ETS_SUCCESS: {
      return update(state, {
        detail: {
          ets: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
          // auto-select the first loaded et
          selectedEventTypeId: { $set: get(action.payload, 'ets[0].id') },
        },
      });
    }

    case GET_PLATFORM_TYPE_ETS_FAILURE: {
      return update(state, {
        detail: {
          ets: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
          // auto-select the first loaded et
          selectedEventTypeId: { $set: undefined },
        },
      });
    }

    case GET_PLATFORM_TYPE_SCHEMAS: {
      return update(state, {
        detail: {
          schemas: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_SCHEMAS_SUCCESS: {
      return update(state, {
        detail: {
          schemas: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_SCHEMAS_FAILURE: {
      return update(state, {
        detail: {
          schemas: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_DIFF: {
      return update(state, {
        detail: {
          diff: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_DIFF_SUCCESS: {
      return update(state, {
        detail: {
          diff: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_DIFF_FAILURE: {
      return update(state, {
        detail: {
          diff: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_VALIDATION: {
      return update(state, {
        detail: {
          validation: {
            isLoading: { $set: true },
            error: { $set: undefined },
            data: { $set: undefined },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_VALIDATION_SUCCESS: {
      return update(state, {
        detail: {
          validation: {
            isLoading: { $set: false },
            error: { $set: undefined },
            data: { $set: action.payload },
          },
        },
      });
    }

    case GET_PLATFORM_TYPE_VALIDATION_FAILURE: {
      return update(state, {
        detail: {
          validation: {
            isLoading: { $set: false },
            error: { $set: action.payload },
            data: { $set: undefined },
          },
        },
      });
    }

    case SET_SELECTED_PI_EVENT_TYPE_ID: {
      return update(state, {
        detail: { selectedEventTypeId: { $set: action.eventId } },
      });
    }

    case SET_SELECTED_MAPPING_LI_ID: {
      return update(state, {
        detail: { selectedMappingLiId: { $set: action.liId } },
      });
    }

    case SAVE_PLATFORM_TYPE_DETAILS_EDITS: {
      return update(state, {
        detail: {
          isSaving: { $set: true },
        },
      });
    }

    case SAVE_PLATFORM_TYPE_DETAILS_EDITS_SUCCESS: {
      // clear any loaded data to force it to reload and thus reflect any saved changes
      // (this is much simpler than attempting to apply deltas on save,
      // but it is a bit slower since everything has to be refetched)
      return update(state, {
        detail: {
          $set: {
            ...getInitialDetailState(),
            inEditMode: action.stayEditMode,
          },
        },
      });
    }
    case SAVE_PLATFORM_TYPE_DETAILS_EDITS_FAILURE: {
      // clear any loaded data to force it to reload and thus reflect any saved changes
      // (this is much simpler than attempting to apply deltas on save,
      // but it is a bit slower since everything has to be refetched)
      return update(state, {
        detail: {
          $set: {
            ...getInitialDetailState(),
            saveError: action.payload,
          },
        },
      });
    }

    case START_PLATFORM_TYPE_DETAILS_EDIT_MODE: {
      return update(state, {
        detail: {
          inEditMode: { $set: true },
        },
      });
    }

    case CANCEL_PLATFORM_TYPE_DETAILS_EDIT_MODE: {
      return update(state, {
        detail: {
          inEditMode: { $set: false },
        },
      });
    }

    case GET_PLATFORM_TYPES_SUCCESS: {
      return update(state, {
        table: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
          data: { $set: action.payload },
          tableState: {
            // we need to pass a set of IDs for the table to the baseTableReducer
            // so that the "select all" button can know which rows to select.  this
            // data-of-ids should track with the data rows rendered in the table itself.
            data: {
              $set: action.payload.results.map(i => ({
                id: `${i.typeId}:${i.deviceId}`,
              })),
            },
            view: {
              pagination: {
                totalItems: { $set: action.payload.meta.total_rows },
              },
            },
          },
        },
      });
    }
    case GET_PLATFORM_TYPES:
      return {
        ...state,
        table: {
          ...state.table,
          fetchingData: action.type,
          fetchDataError: null,
        },
      };
    case GET_PLATFORM_TYPES_FAILURE:
      return {
        ...state,
        table: {
          ...state.table,
          fetchingData: false,
          fetchDataError: action.payload,
        },
      };

    case DELETE_PLATFORM_TYPE:
      return update(state, {
        detail: {
          fetchingData: { $set: action.type },
          fetchDataError: { $set: null },
        },
      });

    case DELETE_PLATFORM_TYPE_SUCCESS:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: null },
        },
        redirectTo: { $set: ROUTES.CONNECT_TYPES },
      });

    case DELETE_PLATFORM_TYPE_FAILURE:
      return update(state, {
        detail: {
          fetchingData: { $set: false },
          fetchDataError: { $set: action.payload },
        },
      });

    case UPDATE_TYPE_SHOW_DELETE_CONFIRM:
      return update(state, {
        showDeleteConfirm: { $set: action.show },
      });

    case TYPES_TABLE_SET_PAGE:
      return update(state, {
        table: {
          tableState: {
            view: {
              pagination: {
                page: { $set: action.page },
              },
            },
          },
        },
      });

    case TYPES_TABLE_CLEAR_REDIRECT:
      return update(state, {
        redirectTo: { $set: undefined },
      });

    case TYPES_TABLE_CLEAR_SEARCH:
      return update(state, {
        table: {
          tableState: {
            view: {
              filters: { $set: [] },
              toolbar: {
                search: { $set: { defaultValue: undefined } },
              },
            },
          },
        },
        initialTableSearchDefaultValue: { $set: undefined },
      });

    case tableActions.TABLE_SEARCH_APPLY:
      if (
        typeof action.instanceId === 'string' &&
        action.instanceId !== state.table.tableState.instanceId
      ) {
        return state;
      }

      return update(state, {
        initialTableSearchDefaultValue: { $set: action.payload },
      });

    case tableActions.TABLE_FILTER_CLEAR:
      if (
        typeof action.instanceId === 'string' &&
        action.instanceId !== state.table.tableState.instanceId
      ) {
        return state;
      }
      return update(state, {
        table: {
          tableState: {
            view: {
              filters: {
                $set: [],
              },
              pagination: {
                page: { $set: 1 },
              },

              toolbar: {
                search: {
                  defaultValue: { $set: '' },
                  value: { $set: undefined },
                },
              },
            },
          },
        },
      });

    default: // no-op - might be a table action
  }

  // To support instance matching (i.e. multiple table states in one store), just ensure
  // that a instanceId is provided on state passed to baseTableReducer.  If no instanceId
  // is provided, all matching actions will be processed by this table reducer.
  if (
    typeof action.instanceId === 'string' &&
    action.instanceId !== state.table.tableState.instanceId
  ) {
    return state;
  }

  switch (action.type) {
    case tableActions.TABLE_ACTION_CANCEL:
    case tableActions.TABLE_ACTION_APPLY:
    case tableActions.TABLE_ROW_CLICK:
    case tableActions.TABLE_COLUMN_SORT:
    case tableActions.TABLE_ROW_SELECT_ALL:
    case tableActions.TABLE_ROW_SELECT:
    case tableActions.TABLE_TOOLBAR_TOGGLE:
    case tableActions.TABLE_FILTER_APPLY:
    case tableActions.TABLE_EMPTY_STATE_ACTION:
    case tableActions.TABLE_COLUMN_ORDER:
    case tableActions.TABLE_ROW_EXPAND:
    case tableActions.TABLE_PAGE_CHANGE:
      return {
        ...state,
        table: {
          ...state.table,
          tableState: baseTableReducer(state.table.tableState, action),
        },
      };
    default:
      return state;
  }
};

// selectors
export const getDeviceTypes = state => get(state, 'deviceTypes.table.data.results') || [];
export const getDeviceTypesBookmark = state =>
  get(state, 'deviceTypes.table.data.bookmark') || null;
export const getDeviceTypesTotalCount = state =>
  get(state, 'deviceTypes.table.data.meta.total_rows') || null;
export const isFetchingDeviceTypes = state =>
  get(state, 'deviceTypes.table.fetchingData') !== false;
export const fetchDeviceTypesError = state => get(state, 'deviceTypes.table.fetchDataError');
export const getDeviceTypeTableView = state => get(state, 'deviceTypes.table.tableState.view');
export const getDeviceTypeTableFilters = state =>
  get(state, 'deviceTypes.table.tableState.view.filters');
export const getDeviceTypeTableSearch = state =>
  get(state, 'deviceTypes.table.tableState.view.toolbar.search');

export const getDeviceType = state => get(state, `deviceTypes.detail.type`) || undefined;
export const isFetchingDeviceType = state =>
  get(state, 'deviceTypes.detail.fetchingData') === GET_PLATFORM_TYPE;
export const fetchDeviceTypeError = state => get(state, 'deviceTypes.detail.fetchDataError');

export const getDeviceTypeTableSort = state =>
  get(state, 'deviceTypes.table.tableState.view.table.sort');
export const getDeviceTypeShowDeleteConfirm = state => get(state, 'deviceTypes.showDeleteConfirm');
export const getDeviceTypeTableSelectedRowIds = state =>
  get(state, 'deviceTypes.table.tableState.view.table.selectedIds');
export const getDeviceTypeTableInstanceId = state =>
  get(state, 'deviceTypes.table.tableState.instanceId');

export const getPi = state => get(state, 'deviceTypes.detail.pi');
export const getEtms = state => get(state, 'deviceTypes.detail.etms');
export const getEts = state => get(state, 'deviceTypes.detail.ets');
export const isSavingDeviceType = state => get(state, 'deviceTypes.detail.isSaving');
export const inEditMode = state => get(state, 'deviceTypes.detail.inEditMode');
export const getSelectedEventTypeId = state => get(state, 'deviceTypes.detail.selectedEventTypeId');
export const getSelectedMappingLiId = state => get(state, 'deviceTypes.detail.selectedMappingLiId');

export const getDeviceTypeDiff = state => get(state, 'deviceTypes.detail.diff');
export const getDeviceTypeValidation = state => get(state, 'deviceTypes.detail.validation');
export const getLi = state => get(state, 'deviceTypes.detail.li');
export const getRedirectTo = state => get(state, 'deviceTypes.redirectTo');

export const getTypeLis = state => get(state, 'deviceTypes.detail.lis');
export const getTypeMappings = state => get(state, 'deviceTypes.detail.mappings');
export const getTypeSchemas = state => get(state, 'deviceTypes.detail.schemas');

export const getType = state => get(state, `deviceTypes.detail.type`) || undefined;

export const getSaveError = state => get(state, `deviceTypes.detail.saveError`) || undefined;
export const getInitialTableSearchDefaultValue = state =>
  get(state, 'deviceTypes.initialTableSearchDefaultValue');
